import * as React from 'react';
import cn from 'classnames';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';

import { useWriteStatusQuery } from 'src/api/queries/subscriptionQueries';
import useRoleManager from 'src/hooks/useRoleManager';

import styles from '../BillingBanner/BillingBanner.module.scss';

const messages = defineMessages({
  out: {
    id: 'creditAlertBanner.out',
    defaultMessage:
      "You're out of credits. New videos are stored but not processed, and AI actions are paused.",
  },
  outWithCount: {
    id: 'creditAlertBanner.outWithCount',
    defaultMessage:
      "You're out of credits: {count, plural, one {# item} other {# items}} couldn't be processed. New videos are stored but not processed, and AI actions are paused.",
  },
  low: {
    id: 'creditAlertBanner.low',
    defaultMessage: 'Credits are running low: {balance} left.',
  },
  addCredits: {
    id: 'creditAlertBanner.addCredits',
    defaultMessage: 'Add credits',
  },
  askOwnerOut: {
    id: 'creditAlertBanner.askOwnerOut',
    defaultMessage: 'Ask your account owner to add credits.',
  },
  askOwnerLow: {
    id: 'creditAlertBanner.askOwnerLow',
    defaultMessage: 'Let your account owner know.',
  },
  dismiss: {
    id: 'creditAlertBanner.dismiss',
    defaultMessage: 'Dismiss',
  },
});

// Opens the buy-credits control on the Subscription page.
export const ADD_CREDITS_ROUTE = '/admin/subscription?purchase=credits';

// A dismissed low-credit banner stays hidden for this browser session, per
// episode: a NEW low episode (credits added, then low again) shows again.
const DISMISS_KEY = 'creditAlertBanner.dismissedLow';

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

/**
 * App-wide low-/out-of-credit banner (credits pay for AI and, since conf/22,
 * video processing).
 *
 * Reads `GET /api/v1/account/write-status`, which every member can read —
 * `BillingBanner` reads the owner/admin-only subscription and so is silent
 * for everyone else, but a creator whose upload sat unprocessed needs to know
 * why. The server returns `credit_alert: null` for the User role, so this
 * renders nothing for them without a role check here.
 *
 * Admins (who can buy credits) get the "Add credits" link; Managers and
 * Creators are told to ask the account owner. `out` is red and stays; `low`
 * is amber and can be dismissed for the session.
 */
export const CreditAlertBanner: React.FC = () => {
  const { data } = useWriteStatusQuery();
  const { isAdmin } = useRoleManager();
  const [dismissed, setDismissed] = React.useState<string | null>(readDismissed);

  const alert = data?.credit_alert;
  if (!alert) return null;

  const isOut = alert.kind === 'out';
  const episodeKey = `${alert.kind}:${alert.since ?? ''}`;
  if (!isOut && dismissed === episodeKey) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, episodeKey);
    } catch {
      // private window: dismiss for this render tree only
    }
    setDismissed(episodeKey);
  };

  return (
    <div
      className={cn(styles.banner, isOut ? styles.usageOver : styles.usageWarn)}
      role={isOut ? 'alert' : 'status'}
      data-test="credit-alert-banner"
      data-kind={alert.kind}
    >
      <span>
        {isOut ? (
          alert.blocked_count > 0 ? (
            <FormattedMessage {...messages.outWithCount} values={{ count: alert.blocked_count }} />
          ) : (
            <FormattedMessage {...messages.out} />
          )
        ) : (
          <FormattedMessage
            {...messages.low}
            values={{ balance: <FormattedNumber value={alert.balance} /> }}
          />
        )}
        {!isAdmin && (
          <>
            {' '}
            <FormattedMessage {...(isOut ? messages.askOwnerOut : messages.askOwnerLow)} />
          </>
        )}
      </span>
      {isAdmin && (
        <Link className={styles.link} to={ADD_CREDITS_ROUTE}>
          <FormattedMessage {...messages.addCredits} />
        </Link>
      )}
      {!isOut && (
        <button type="button" className={styles.linkButton} onClick={dismiss}>
          <FormattedMessage {...messages.dismiss} />
        </button>
      )}
    </div>
  );
};

export default CreditAlertBanner;
